// Google Maps style element type
/** One basemap rule in the Maps SDK's shape, with its stylers narrowed to
 *  the string-valued bag this module writes (`color`, `visibility`, …) so a
 *  reader can key on a color without a cast. */
type MapStyleElement = Omit<google.maps.MapTypeStyle, "stylers"> & {
  stylers: Array<Record<string, string>>;
};

// The few colors that stay fixed live in the JSON file — the architecture
// guard only scans .ts/.tsx/.js/.jsx, so keeping literal color values in JSON
// avoids false positives. Everything else on the map derives from the theme:
// DOM-rendered chrome consumes `var(--token)` directly, and the tile styler
// (which cannot read CSS) is generated from the resolved theme in
// `buildMapTileTheme` below.
import mapData from "./map-google-styles.json";
import {
  oklchToHex,
  parseOklch,
  type CustomThemeProperties,
  type Oklch,
  type Omittable,
} from "@salestrak/ui";

// Map defaults from packages/defaults/map.js
export const DEFAULT_CENTER = {
  latitude: 43.6079116,
  longitude: -116.3034948,
};

export const DEFAULT_ZOOM = 15;
export const MIN_ZOOM = 4;
export const MAX_ZOOM = 22;

/** Convert a Google Maps zoom level to a latitudeDelta (degrees of latitude spanned by viewport) */
export function zoomToLatitudeDelta(zoom: number): number {
  return 360 / Math.pow(2, zoom);
}

/** Convert a latitudeDelta (degrees of latitude spanned by viewport) to a Google Maps zoom level */
export function latitudeDeltaToZoom(latDelta: number): number {
  return Math.log2(360 / latDelta);
}

/** Build a default Region for the initial map view */
export const DEFAULT_REGION = {
  ...DEFAULT_CENTER,
  latitudeDelta: zoomToLatitudeDelta(DEFAULT_ZOOM),
  longitudeDelta: zoomToLatitudeDelta(DEFAULT_ZOOM) * 1.5,
};

// ─── Theme-aware map colors ──────────────────────────────────────────────────

type MapColors = {
  /** Separation halo around pins and cluster bubbles, over the tiles. */
  outlineColor: string;
  /** Scrim under a pin's count/icon so the glyph reads on any identity color. */
  pinOverlay: string;
  pinOverlayText: string;
  clusterFill: string;
  clusterText: string;
  isDark: boolean;
};

/**
 * Theme-aware colors for the map's MARKERS (pure function, no hooks).
 * Markers are ordinary DOM, so they take `var()` strings and re-resolve live
 * on every theme, mode and accent change. Only the scrim pair stays literal:
 * it sits on an identity-colored disc, which wears white ink in both modes,
 * so no theme token can name it. The floating CONTROLS take their colors
 * from the tile theme instead ({@link MapTileTheme}) — a token rung sits too
 * close to the tile ground to separate from it.
 */
export function getMapColors(isDark: boolean): MapColors {
  return {
    // Light floats markers on a paper halo; dark separates with a visible
    // ring instead — a page-colored halo would vanish against dark tiles.
    outlineColor: isDark ? "var(--faint-foreground)" : "var(--card)",
    pinOverlay: isDark
      ? mapData.colors.pinOverlay.dark
      : mapData.colors.pinOverlay.light,
    pinOverlayText: mapData.colors.pinOverlayText,
    // A cluster is an ink chip in light and a lifted surface chip in dark —
    // the same grammar the authored palette drew, restated in tokens.
    clusterFill: isDark ? "var(--elevated)" : "var(--primary)",
    clusterText: isDark ? "var(--foreground)" : "var(--primary-foreground)",
    isDark,
  };
}

// ─── Themed tile styler ──────────────────────────────────────────────────────
//
// Google's styler consumes literal hex — the one map surface `var(--token)`
// cannot reach — so the basemap is GENERATED from the resolved theme instead
// of shipped as two frozen palettes. The shape of the map is a table of OKLCH
// lightness offsets measured from the authored light/dark palettes; the theme
// supplies the position (the page ground) and the cast (the ground's and the
// muted ink's hue/chroma), so a tinted theme tints the tiles the way it tints
// every other surface.

/** Everything the map derives its tiles from. */
export type MapTileTheme = {
  style: MapStyleElement[];
  /** The tile ground as hex — what Google paints while tiles load. */
  groundHex: string;
  /**
   * Opaque fill for the floating controls (search, docks). Steps toward the
   * mode's pole — deeper than the PAGE in dark (every shade keeps the tiles
   * at or above the page's neighborhood), whiter than the tiles in light —
   * so a control never melts into the tiles the way a near-ground token rung
   * does, and the dark fill holds still while the shade moves the tiles.
   */
  controlBg: string;
  /**
   * Hairline around the floating controls. What the map paints under a
   * control is arbitrary once imagery loads (terrain, satellite), so the
   * fill alone cannot guarantee an edge — this line, legible against both
   * the control fill and the ground, is the guaranteed separation.
   */
  controlBorder: string;
};

/**
 * Feature offsets from the tile ground, in OKLCH lightness points. Two tables
 * because the two polarities travel differently: light recesses everything
 * under a near-white ground, dark lifts detail off a near-black one. Values
 * are measured from the reference palettes, so the shipped theme reproduces
 * the reference map.
 */
type TileDeltas = {
  labelStroke: number;
  labelStrong: number;
  labelMedium: number;
  labelSoft: number;
  admin: number;
  manMade: number;
  building: number;
  roadFill: number;
  roadStroke: number;
  arterial: number;
  highway: number;
  highwayControlled: number;
  localRoad: number;
  transitLine: number;
  park: number;
  parkLabel: number;
  parkLabelStroke: number;
  water: number;
  waterLabel: number;
};

const LIGHT_DELTAS: TileDeltas = {
  labelStroke: 3,
  labelStrong: -52,
  labelMedium: -42.5,
  labelSoft: -37,
  admin: -9.4,
  manMade: -0.9,
  building: -8.5,
  roadFill: -9.1,
  roadStroke: -11.6,
  arterial: -8.5,
  highway: -8.2,
  highwayControlled: -10.6,
  localRoad: -10.2,
  transitLine: -6.3,
  park: -2.5,
  parkLabel: -32,
  parkLabelStroke: 3,
  water: -6.8,
  waterLabel: -34.5,
};

const DARK_DELTAS: TileDeltas = {
  labelStroke: 0,
  labelStrong: 62,
  labelMedium: 58.5,
  labelSoft: 51,
  admin: 5.5,
  manMade: 7.6,
  building: 16.9,
  roadFill: 1.7,
  roadStroke: -2.2,
  arterial: 3.4,
  highway: 1.3,
  highwayControlled: -2.2,
  localRoad: 1.7,
  transitLine: 3.4,
  park: 5.5,
  parkLabel: 51,
  parkLabelStroke: 3.4,
  water: -2.2,
  waterLabel: 51,
};

/**
 * Semantic hues keep their identity in light mode (a park is green, water is
 * blue) at the theme's lightness; the dark palette draws both as neutral
 * ground steps, so it carries no tints. Values are the authored palette's.
 */
type TileTint = { c: number; h: number };
type TileTints = Omittable<{
  park?: TileTint;
  parkLabel?: TileTint;
  water?: TileTint;
  waterLabel?: TileTint;
}>;

const LIGHT_TINTS: TileTints = {
  park: { c: 0.028, h: 142 },
  parkLabel: { c: 0.086, h: 145 },
  water: { c: 0.035, h: 244 },
  waterLabel: { c: 0.105, h: 255 },
};

const DARK_TINTS: TileTints = {};

/**
 * Resting palette for when the appearance tokens fail to resolve (an
 * unparsable stored seed): the shipped design's light page ground and dark
 * SUNKEN rung (the dark map recedes — see `buildMapTileTheme`), and the
 * muted ink.
 */
const FALLBACK_GROUND: Record<"light" | "dark", Oklch> = {
  light: { l: 98.21, c: 0, h: 0 },
  dark: { l: 18.35, c: 0, h: 0 },
};
const FALLBACK_LABEL_INK: Record<"light" | "dark", Oklch> = {
  light: { l: 50.33, c: 0, h: 0 },
  dark: { l: 74.02, c: 0, h: 0 },
};

const clampL = (l: number): number => Math.min(100, Math.max(0, l));

/** The dark ground's normal-shade lift off the page (+6.7L lands between the
 *  popover and its strong hairline — the raised reference-map look), and the
 *  lighter shade's further step. The darker shade is the sunken rung. */
const RAISED_GROUND_LIFT = 6.7;
const LIGHTER_GROUND_LIFT = 13.5;
/** Light mode's headroom is one-sided: the page sits ~2L under white, so
 *  lighter clamps to pure white and the scale's real travel is the darker
 *  drop — wide enough to read as a different ground, not a tint. */
const LIGHT_MODE_DARKER_DROP = 12;
const LIGHT_MODE_LIGHTER_LIFT = 4;

/**
 * Builds the styled basemap for the resolved appearance tokens. `isDark` is
 * only the fallback polarity — with readable tokens the tables are picked by
 * the GROUND's own lightness, so a dark seed painted in light mode still gets
 * tiles its labels can read on.
 */
export function buildMapTileTheme(
  tokens: CustomThemeProperties | null,
  isDark: boolean,
  shade: MapShade = "lighter",
): MapTileTheme {
  const fallbackMode = isDark ? "dark" : "light";
  const page =
    (tokens && parseOklch(tokens.background)) ?? FALLBACK_GROUND[fallbackMode];
  const darkGround = page.l < 50;
  // Tiles at the page's own value left only ~2L under the popover chrome, so
  // the dark ground moves off it; `shade` picks how far, always within the
  // CURRENT mode's family. Light mode's normal shade keeps the page ground —
  // a near-white page has little headroom either way.
  const ground = !darkGround
    ? {
        ...page,
        l: clampL(
          page.l +
            (shade === "darker"
              ? -LIGHT_MODE_DARKER_DROP
              : shade === "lighter"
                ? LIGHT_MODE_LIGHTER_LIFT
                : 0),
        ),
      }
    : shade === "darker"
      ? ((tokens && parseOklch(tokens.sunken)) ?? FALLBACK_GROUND.dark)
      : {
          ...page,
          l: clampL(
            page.l +
              (shade === "lighter" ? LIGHTER_GROUND_LIFT : RAISED_GROUND_LIFT),
          ),
        };
  const labelInk =
    (tokens && parseOklch(tokens["muted-foreground"])) ??
    FALLBACK_LABEL_INK[darkGround ? "dark" : "light"];
  const deltas = darkGround ? DARK_DELTAS : LIGHT_DELTAS;
  const tints = darkGround ? DARK_TINTS : LIGHT_TINTS;

  // A ground plane stepped off the page, wearing the page's own cast.
  const plane = (delta: number): string =>
    oklchToHex({ l: clampL(ground.l + delta), c: ground.c, h: ground.h });
  // A label ink at the table's rung, wearing the muted ink's cast.
  const ink = (delta: number): string =>
    oklchToHex({ l: clampL(ground.l + delta), c: labelInk.c, h: labelInk.h });
  // A semantic tint (park green, water blue) at the theme's lightness.
  const tint = (delta: number, hue: TileTint | undefined): string =>
    hue
      ? oklchToHex({ l: clampL(ground.l + delta), c: hue.c, h: hue.h })
      : plane(delta);
  const color = (value: string) => [{ color: value }];

  const style: MapStyleElement[] = [
    { elementType: "geometry", stylers: color(plane(0)) },
    { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
    { elementType: "labels.text.fill", stylers: color(ink(deltas.labelStrong)) },
    { elementType: "labels.text.stroke", stylers: color(plane(deltas.labelStroke)) },
    {
      featureType: "administrative",
      elementType: "geometry",
      stylers: color(plane(deltas.admin)),
    },
    {
      featureType: "administrative.country",
      elementType: "labels.text.fill",
      stylers: color(ink(deltas.labelMedium)),
    },
    {
      featureType: "administrative.locality",
      elementType: "labels.text.fill",
      stylers: color(ink(deltas.labelStrong)),
    },
    {
      featureType: "landscape.man_made",
      elementType: "geometry",
      stylers: color(plane(deltas.manMade)),
    },
    {
      featureType: "landscape.man_made.building",
      elementType: "geometry.fill",
      stylers: color(plane(deltas.building)),
    },
    {
      featureType: "landscape.man_made.building",
      elementType: "geometry.stroke",
      stylers: color(plane(deltas.building)),
    },
    {
      featureType: "poi",
      elementType: "labels.text.fill",
      stylers: color(ink(deltas.labelMedium)),
    },
    {
      featureType: "poi.park",
      elementType: "geometry",
      stylers: color(tint(deltas.park, tints.park)),
    },
    {
      featureType: "poi.park",
      elementType: "labels.text.fill",
      stylers: color(tint(deltas.parkLabel, tints.parkLabel)),
    },
    {
      featureType: "poi.park",
      elementType: "labels.text.stroke",
      stylers: color(plane(deltas.parkLabelStroke)),
    },
    {
      featureType: "road",
      elementType: "geometry.fill",
      stylers: color(plane(deltas.roadFill)),
    },
    {
      featureType: "road",
      elementType: "geometry.stroke",
      stylers: color(plane(deltas.roadStroke)),
    },
    {
      featureType: "road",
      elementType: "labels.text.fill",
      stylers: color(ink(deltas.labelStrong)),
    },
    {
      featureType: "road.arterial",
      elementType: "geometry",
      stylers: color(plane(deltas.arterial)),
    },
    {
      featureType: "road.highway",
      elementType: "geometry",
      stylers: color(plane(deltas.highway)),
    },
    {
      featureType: "road.highway.controlled_access",
      elementType: "geometry",
      stylers: color(plane(deltas.highwayControlled)),
    },
    {
      featureType: "road.local",
      elementType: "geometry",
      stylers: color(plane(deltas.localRoad)),
    },
    {
      featureType: "road.local",
      elementType: "labels.text.fill",
      stylers: color(ink(deltas.labelSoft)),
    },
    {
      featureType: "transit",
      elementType: "labels.text.fill",
      stylers: color(ink(deltas.labelSoft)),
    },
    {
      featureType: "transit.line",
      elementType: "geometry",
      stylers: color(plane(deltas.transitLine)),
    },
    {
      featureType: "water",
      elementType: "geometry",
      stylers: color(tint(deltas.water, tints.water)),
    },
    {
      featureType: "water",
      elementType: "labels.text.fill",
      stylers: color(tint(deltas.waterLabel, tints.waterLabel)),
    },
  ];

  // The authored palettes' own arrangement, generalized: dark controls sat
  // DEEPER than the page (#090909 under the #191919 page), light controls sat
  // at the white pole over the paper tiles. The dark step is taken from the
  // PAGE, not the shaded tile ground: the controls are DOM chrome that shares
  // the page with the popovers, and a fill that tracked the ground climbed
  // past the popover rung on the lighter shade. Every shade lifts the tiles
  // off the page or recesses them to the sunken rung, so a fill 8.5L under
  // the page stays under the tiles on all three. A near-black page has no
  // room below, so the step flips upward there — distinct either way round.
  const controlL = darkGround
    ? page.l >= 13
      ? page.l - 8.5
      : page.l + 8.5
    : Math.min(ground.l + 3, 100);
  const controlBorder = oklchToHex({
    l: clampL(darkGround ? controlL + 18 : controlL - 15),
    c: labelInk.c,
    h: labelInk.h,
  });

  return {
    style,
    groundHex: plane(0),
    controlBg: oklchToHex({ l: clampL(controlL), c: ground.c, h: ground.h }),
    controlBorder,
  };
}

// ─── Map settings (configurable through Customize view) ───────────────────

/** The styled basemap's SHADE — a lighter or darker variant of the current
 *  mode's own ground, never the other mode's palette (a cross-theme map
 *  dragged the control fills with it and clashed with the themed popovers).
 *  In dark: darker = the recessed sunken rung, "default" = the raised mid
 *  ground, lighter = a step further up. Light mode shifts the same way,
 *  within the little headroom a near-white page has. Lighter is what new
 *  settings ship with; the "default" value wears the label "Normal" and keeps
 *  its name only because saved map settings persist it. */
export type MapShade = "default" | "lighter" | "darker";

export type MapSettings = {
  mapTypeId: "terrain" | "hybrid";
  /** Basemap shade (see {@link MapShade}). DOM chrome ignores it. */
  mapShade: MapShade;
  roadNames: boolean;
  pointsOfInterest: boolean;
  transitLines: boolean;
  /** Google's live traffic overlay — a layer over either base map. */
  traffic: boolean;
  /** Satellite's one label switch: Google's hybrid label layer is
   *  all-or-nothing (JSON styles apply to the styled map only), so on
   *  imagery the three per-kind toggles collapse into this — on = hybrid,
   *  off = bare imagery. Inert while the styled map is the base. */
  satelliteLabels: boolean;
  /** A dark scrim over satellite imagery so pins, cards and territory strokes
   *  stop competing with bright rooftops. Off by default: imagery is the one
   *  layer readers expect to be true, and a dim costs roof and lot detail at
   *  the zooms where they inspect a property. An overlay layer like traffic —
   *  inert while the base is the styled map. */
  dimSatellite: boolean;
  /** Melissa property data on the pin infobox (facts line + expansion) and
   *  the mobile sheet. A detail layer like road names — DOM-only, so it
   *  never remounts the Map. */
  propertyFacts: boolean;
  accuracySensors: boolean;
};

export const DEFAULT_MAP_SETTINGS: MapSettings = {
  mapTypeId: "terrain",
  mapShade: "lighter",
  roadNames: true,
  pointsOfInterest: false,
  transitLines: false,
  traffic: false,
  satelliteLabels: true,
  dimSatellite: false,
  propertyFacts: true,
  accuracySensors: false,
};

// ─── Satellite dim overlay ───────────────────────────────────────────────────

/** The scrim's strength. Light enough that imagery still reads as
 *  imagery; strong enough that a white rooftop no longer out-shouts a pin.
 *  Neither Apple nor Google dims satellite at all, so this is a deliberate
 *  reader choice (the "Dim satellite imagery" switch), never a theme
 *  default. */
export const SATELLITE_DIM_OPACITY = 0.15;

/** One solid black pixel, stretched to the tile — every dim tile is the same
 *  image, so the overlay costs no network and no per-tile draw. */
export const SATELLITE_DIM_TILE_URL =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAIAAACQd1PeAAAADElEQVR4nGNgYGAAAAAEAAH2FzhVAAAAAElFTkSuQmCC";

